import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable ,  EMPTY as empty ,  BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorHandlerService, HandleError } from './http-error-handling.service';
import { EventEmitter } from 'events';
import { TypesService } from './types.service';
import * as _ from 'lodash';

const httpOptions = {
    headers: new HttpHeaders({
      'Content-Type':  'application/json',
      'Authorization': 'my-auth-token'
    })
  };

  @Injectable()
export class RateService {
  private handleError: HandleError;

  private rateDataSubject = new BehaviorSubject<PricingOutput>(null);
  public rateData = this.rateDataSubject.asObservable();

  private rateParametersSubject = new BehaviorSubject<PricingInput>(null);
  public rateParameters = this.rateParametersSubject.asObservable();

  constructor(private oauthService: OAuthService,
              private http: HttpClient,
              private typesService: TypesService,
              httpErrorHandler: HttpErrorHandlerService) {
    httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
    this.handleError = httpErrorHandler.createHandleError('RateService');
  }

  public getDialDownRates(type, inputData: PricingInput) {
    // make sure required data are filled up before submitting to endpoint
    const requiredFields = ['year', 'primaryPurpose', 'baseRate'];
    const missing = requiredFields.find(key => inputData[key] === null || inputData[key] === undefined);

    if (missing) { return empty; }
    // delete output if it was passed
    delete inputData.output;

    return this.http
      .post<any>(`${environment.api.product}/dial-down-rates/${type}`, inputData, httpOptions)
      .pipe(
        map((res) => {
          this.rateDataSubject.next(res.results);
          return res.results;
        }),
        catchError(this.handleError('Get Rates', []))
      );
  }

  public getDialDownRatesBasic(type, inputData: PricingInput) {
    return this.http
      .post<any>(`${environment.api.product}/dial-down-rates/${type}`, inputData, httpOptions)
      .pipe(
        map((res) => {
          return res.results;
        }),
        catchError(this.handleError('Get Rates', []))
      );
  }

  public setRateParameters(params: PricingInput) {
    this.rateParametersSubject.next(params);
  }

  public setRateData(params: PricingOutput) {
    this.rateDataSubject.next(params);
  }

  getDialDownEnabled() {
    return this.typesService.getTypesList('submission-configurations').then((res) => {
      const configs = res;
      const brokeragePaymentType = _(configs).chain().find(t => t.id === 'fee-defaults').get('ext.brokerage.paymentType').value();
      if (brokeragePaymentType && brokeragePaymentType === 'funded') {
        // this.formGroup.get('brokeragePaymentType').patchValue(brokeragePaymentType);
        return Promise.resolve(false);
      }
      return Promise.resolve(true);
    });
  }
}

export interface PricingInput {
  channel?: string;
  year?: number;
  make?: string;
  model?: string;
  variantName?: string;
  fuelName?: string;
  interestType?: string;
  brokeragePaymentType?: string;
  transmissionType?: string;
  odometerReading?: number;
  depositAmount?: number;
  totalLoanAmount?: number;
  capitalisedPurposes?: number;
  realEstateCountWithoutNas?: number;
  balloonAmount?: number;
  brokerage?: number;
  primaryPurpose?: string;
  applicationFee?: number;
  originationFee?: number;
  brokerageIncludesGST?: boolean;
  month?: any;
  engineCapacity?: null;
  engineName?: null;
  baseRate?: number;
  termInYears?: number;
  fixedTermYrs?: number;
  referenceRate?: string;
  pricingSlug?: string;
  linkedAppCount?: number;
  output?: any;
}

export interface PricingOutput {
  FirstmacMaxBorrowingRate?: number;
  TotalFinancedAmount?: number;
  RetailAdjustment?: number;
  TPSRateToBorrower?: number;
  AdjustedBrokerageAmount?: number;
  MaxBrokerageAmountAvailable?: number;
  MaxTotalMonthlyRepayments?: number;
  BrokerageToBePaid?: number;
  BrokerageGST?: number;
  TotalBrokerageInclGST?: number;
  IsGreen?: string;
  AdjustedTotalMonthlyRepayments?: number;
  maxInterestRateInput?: number;
  minInterestRateInput?: number;
}

